import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type {
  KaiRestrictionProposalPreview,
  RestrictionProposalAction,
} from "./kaiTypes";

const ACTION_BADGE_VARIANT: Record<
  RestrictionProposalAction,
  "default" | "urgent" | "destructive" | "secondary"
> = {
  create: "default",
  update: "urgent",
  delete: "destructive",
  enable: "default",
  disable: "secondary",
};

const ACTION_LABEL: Record<RestrictionProposalAction, string> = {
  create: "Create",
  update: "Update",
  delete: "Delete",
  enable: "Enable",
  disable: "Disable",
};

function ruleTypeLabel(
  ruleType: string | null | undefined,
  explicit?: string,
): string {
  if (explicit) return explicit;
  if (!ruleType) return "—";
  return ruleType;
}

function DiffValue({ before, after }: { before: ReactNode; after: ReactNode }) {
  return (
    <span>
      <span className="text-destructive">{before}</span> →{" "}
      <span className="text-emerald-600 dark:text-emerald-400">{after}</span>
    </span>
  );
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function severityValue(severity: string | null | undefined): ReactNode {
  const sev = (severity ?? "info").toLowerCase();
  const variant = sev === "blocked" ? "destructive" : sev === "warning" ? "urgent" : "secondary";
  const note =
    sev === "blocked"
      ? " · blocks the opportunity"
      : " · advisory only (does not block)";
  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant={variant} className="text-[10px]">
        {sev.charAt(0).toUpperCase() + sev.slice(1)}
      </Badge>
      <span className="text-xs text-muted-foreground">{note}</span>
    </span>
  );
}

/**
 * Validate an untyped elicitation preview payload as a `restriction_proposal`.
 */
export function parseRestrictionProposal(
  payload: unknown,
): KaiRestrictionProposalPreview | null {
  if (!payload || typeof payload !== "object") return null;
  const p = payload as Record<string, unknown>;
  const action = p.action;
  if (
    action !== "create" &&
    action !== "update" &&
    action !== "delete" &&
    action !== "enable" &&
    action !== "disable"
  ) {
    return null;
  }
  return p as unknown as KaiRestrictionProposalPreview;
}

function MetaItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

function ParamsBlock({
  title,
  params,
  defaultOpen,
}: {
  title: string;
  params: Record<string, unknown>;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="rounded-lg border border-border/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs font-medium text-foreground/80"
      >
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
        {title}
      </button>
      {open && (
        <pre className="overflow-x-auto border-t border-border/40 px-3 py-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap">
          {JSON.stringify(params, null, 2)}
        </pre>
      )}
    </div>
  );
}

/**
 * Structured restriction-proposal preview shown inside an elicitation
 * confirmation. Action badge, rule type, proposed values (before → after for
 * updates), expandable params, and a callout that confirming applies the
 * change to the live rules immediately.
 */
export function KaiRestrictionProposalCard({
  spec,
}: {
  spec: KaiRestrictionProposalPreview;
}) {
  const items: Array<{ label: string; value: ReactNode }> = [];

  if (spec.action === "create") {
    items.push({
      label: "Rule type",
      value: (
        <Badge variant="default">
          {ruleTypeLabel(spec.rule_type, spec.rule_type_label)}
        </Badge>
      ),
    });
    if (spec.name) items.push({ label: "Name", value: spec.name });
    items.push({ label: "Severity", value: severityValue(spec.severity_override) });
    if (spec.priority != null)
      items.push({ label: "Priority", value: String(spec.priority) });
    if (spec.enabled != null)
      items.push({ label: "Enabled", value: spec.enabled ? "Yes" : "No" });
    if (spec.description)
      items.push({ label: "Description", value: spec.description });
  } else if (spec.action === "delete") {
    items.push({
      label: "Target rule",
      value:
        spec.current?.name ||
        ruleTypeLabel(spec.current?.rule_type) ||
        spec.target_rule_id ||
        "—",
    });
    if (spec.current?.rule_type)
      items.push({
        label: "Rule type",
        value: <Badge variant="destructive">{ruleTypeLabel(spec.current.rule_type)}</Badge>,
      });
  } else {
    items.push({
      label: "Target rule",
      value:
        spec.current?.name ||
        ruleTypeLabel(spec.current?.rule_type) ||
        spec.target_rule_id ||
        "—",
    });
    const changes: Array<[string, unknown, unknown]> = [
      ["Name", spec.current?.name, spec.name],
      ["Severity", spec.current?.severity_override, spec.severity_override],
      ["Enabled", spec.current?.enabled, spec.enabled],
      ["Priority", spec.current?.priority, spec.priority],
      ["Description", spec.current?.description, spec.description],
    ];
    for (const [label, before, after] of changes) {
      if (after === null || after === undefined) continue;
      if (before === after) continue;
      items.push({
        label,
        value: (
          <DiffValue before={displayValue(before)} after={displayValue(after)} />
        ),
      });
    }
  }

  const hasParams = spec.params != null && Object.keys(spec.params).length > 0;
  const hasCurrentParams =
    spec.current?.params != null && Object.keys(spec.current.params).length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Badge variant={ACTION_BADGE_VARIANT[spec.action]}>
          {ACTION_LABEL[spec.action]}
        </Badge>
      </div>

      {spec.rationale && (
        <div className="flex flex-col gap-0.5">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Rationale
          </span>
          <p className="text-sm text-foreground/90">{spec.rationale}</p>
        </div>
      )}

      {items.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          {items.map((item, i) => (
            <MetaItem key={i} label={item.label} value={item.value} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No field changes.</p>
      )}

      {hasParams && (
        <ParamsBlock
          title="Proposed parameters"
          params={spec.params as Record<string, unknown>}
          defaultOpen
        />
      )}

      {spec.action !== "create" && hasCurrentParams && (
        <ParamsBlock
          title="Current parameters"
          params={spec.current!.params as Record<string, unknown>}
        />
      )}

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/8 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
        Confirming applies this change to the live restriction rules
        immediately. There is no separate admin-approval step.
      </div>
    </div>
  );
}
